import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { getPriceDisplay } from '../utils/steamPriceHelper';

const DiscountCard = ({ discount, onDelete }) => {
  const { isAuthenticated, user } = useContext(AuthContext);
  const [priceData, setPriceData] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);

  // More robust ownership check
  const discountUserId = discount.user?._id || discount.user;
  const currentUserId = user?._id;
  const isOwner = isAuthenticated && currentUserId && discountUserId && 
    (String(discountUserId) === String(currentUserId));
  
  // Debug log (remove after testing)
  console.log('Discount ownership check:', {
    isAuthenticated,
    discountUserId,
    currentUserId,
    isOwner,
    discountUserObj: discount.user
  });

  // Fetch Steam price data when component mounts (only for Steam games)
  useEffect(() => {
    const fetchPrice = async () => {
      if (discount.platform === 'Steam' && discount.steamGameData?.appId) {
        setLoadingPrice(true);
        try {
          const price = await getPriceDisplay(discount.steamGameData.appId);
          setPriceData(price);
        } catch (error) {
          console.error('Error fetching price:', error);
        } finally {
          setLoadingPrice(false);
        }
      }
    };

    fetchPrice();
  }, [discount.platform, discount.steamGameData?.appId]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    
    // Manual formatting to ensure consistent spacing
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate();
    const year = date.getFullYear();
    
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hoursStr = hours.toString().padStart(2, '0');
    
    return `${month} ${day}, ${year}, ${hoursStr}:${minutes} ${ampm}`;
  };

  const getTimeRemaining = () => {
    const now = new Date();
    const end = new Date(discount.endDate);
    const diff = end - now;

    if (diff <= 0) return 'Expired';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) return `${days}d ${hours}h left`;
    if (hours > 0) return `${hours}h left`;
    return 'Ending soon';
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this discount?')) {
      await onDelete(discount._id);
    }
  };

  // Determine which game data to use
  // Check if steamGameData has actual content (not just empty object)
  const hasSteamData = discount.steamGameData?.appId || discount.steamGameData?.name;

  const gameData = hasSteamData ? discount.steamGameData : discount.customGameData;

  // Extract game name with better fallbacks
  let gameName = 'Unknown Game';
  if (gameData?.name && gameData.name.trim()) {
    gameName = gameData.name.trim();
  } else if (discount.customGameData?.description) {
    // Try to extract game name from description if it follows "Description: [GameName]" pattern
    const descMatch = discount.customGameData.description.match(/^Description\s*:?\s*([A-Za-z0-9\s:]+?)(?:\s+[A-Z][a-z]|\s*$)/i);
    if (descMatch && descMatch[1]) {
      gameName = descMatch[1].trim();
    }
  }

  const gameImage = discount.steamGameData?.headerImage || discount.customGameData?.imageUrl;

  // Clean up description - remove "Description:" prefix if present
  let gameDescription = discount.steamGameData?.shortDescription || discount.customGameData?.description || '';
  if (gameDescription && gameDescription.toLowerCase().startsWith('description')) {
    gameDescription = gameDescription.replace(/^Description\s*:?\s*/i, '').trim();
  }

  return (
    <div className="discount-card">
      {/* Game Image */}
      {gameImage ? (
        <div className="discount-image">
          <img src={gameImage} alt={gameName} />
          <div className="discount-overlay">
            <span className="discount-badge">{discount.discountPercentage}% OFF</span>
          </div>
        </div>
      ) : (
        <div className="discount-image-placeholder">
          <div className="placeholder-icon">🎮</div>
          <div className="discount-overlay">
            <span className="discount-badge">{discount.discountPercentage}% OFF</span>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="discount-content">
        {/* Platform Badge */}
        <div className="discount-platform">
          <span className="platform-badge">{discount.platform}</span>
        </div>

        {/* Game Title */}
        <h3 className="discount-title">{gameName}</h3>

        {/* Description */}
        {gameDescription && (
          <p className="discount-description">
            {gameDescription.length > 150
              ? `${gameDescription.substring(0, 150)}...`
              : gameDescription}
          </p>
        )}

        {/* Developers/Publishers (Steam only) */}
        {discount.steamGameData && (
          <div className="discount-meta">
            {discount.steamGameData.developers && discount.steamGameData.developers.length > 0 && (
              <p className="discount-developer">
                <strong>Developer:</strong> {discount.steamGameData.developers.join(', ')}
              </p>
            )}
          </div>
        )}

        {/* Price Display (Steam only) */}
        {discount.platform === 'Steam' && (
          <div className="discount-price-section">
            {loadingPrice ? (
              <div className="price-loading">
                <span className="price-spinner">⏳</span> Loading price...
              </div>
            ) : priceData ? (
              <div className="price-display">
                {priceData.isFree ? (
                  <div className="price-free">
                    <span className="price-tag">🎮 Free to Play</span>
                  </div>
                ) : (
                  <div className="price-details">
                    <div className="price-label">Current Price:</div>
                    <div className="price-info">
                      {priceData.hasSteamDiscount && (
                        <span className="price-original">{priceData.formattedOriginalPrice}</span>
                      )}
                      <span className="price-current">{priceData.formattedCurrentPrice}</span>
                      {priceData.hasSteamDiscount && (
                        <span className="price-steam-discount">-{priceData.steamDiscountPercent}% on Steam</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="price-unavailable">
                <span className="price-na">💰 Price info unavailable</span>
              </div>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="discount-stats">
          <div className="discount-stat">
            <span className="stat-label">⏰ Ends:</span>
            <span className="stat-value">{formatDate(discount.endDate)}</span>
          </div>
          <div className="discount-stat">
            <span className="stat-label time-remaining">{getTimeRemaining()}</span>
          </div>
        </div>

        {/* User Info */}
        <div className="discount-footer">
          <div className="discount-user">
            <span>
              Posted by{' '}
              <Link to={`/profile/${discount.user.username}`} className="user-link">
                {discount.user.username}
              </Link>
            </span>
          </div>

          {/* Actions */}
          <div className="discount-actions">
            <a
              href={discount.gameLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary btn-sm"
              onClick={(e) => e.stopPropagation()}
            >
              View Deal
            </a>
            {isOwner && (
              <button
                onClick={handleDelete}
                className="btn btn-danger btn-sm btn-icon"
                title="Delete discount"
              >
                🗑️
              </button>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .discount-card {
          background: var(--card-bg);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: var(--shadow);
          transition: transform 0.2s, box-shadow 0.2s;
          cursor: pointer;
          border: 1px solid var(--border-color);
        }

        .discount-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-hover);
        }

        .discount-image {
          position: relative;
          width: 100%;
          height: 200px;
          overflow: hidden;
        }

        .discount-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .discount-image-placeholder {
          position: relative;
          width: 100%;
          height: 200px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .placeholder-icon {
          font-size: 4em;
          opacity: 0.5;
        }

        .discount-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(to bottom, rgba(0,0,0,0.3), transparent);
          display: flex;
          align-items: flex-start;
          justify-content: flex-end;
          padding: 12px;
        }

        .discount-badge {
          background: var(--accent-red);
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          font-weight: bold;
          font-size: 1.2em;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        }

        .discount-content {
          padding: 16px;
        }

        .discount-platform {
          margin-bottom: 8px;
        }

        .platform-badge {
          display: inline-block;
          background: var(--btn-primary);
          color: white;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 0.85em;
          font-weight: 600;
        }

        .discount-title {
          font-size: 1.4em;
          font-weight: bold;
          margin: 8px 0;
          color: var(--text-primary);
        }

        .discount-description {
          color: var(--text-secondary);
          font-size: 0.9em;
          line-height: 1.5;
          margin: 12px 0;
        }

        .discount-meta {
          margin: 12px 0;
          font-size: 0.85em;
          color: var(--text-secondary);
        }

        .discount-developer {
          margin: 4px 0;
        }

        .discount-price-section {
          margin: 12px 0;
          padding: 12px;
          background: linear-gradient(135deg, rgba(103, 58, 183, 0.1), rgba(33, 150, 243, 0.1));
          border-radius: 8px;
          border: 1px solid rgba(103, 58, 183, 0.3);
        }

        .price-loading {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--text-secondary);
          font-size: 0.9em;
        }

        .price-spinner {
          animation: spin 2s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .price-display {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .price-free {
          text-align: center;
        }

        .price-tag {
          display: inline-block;
          background: linear-gradient(135deg, #4CAF50, #45a049);
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          font-weight: bold;
          font-size: 1em;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }

        .price-details {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .price-label {
          font-size: 0.85em;
          color: var(--text-secondary);
          font-weight: 600;
        }

        .price-info {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .price-original {
          font-size: 1em;
          color: var(--text-secondary);
          text-decoration: line-through;
          opacity: 0.7;
        }

        .price-current {
          font-size: 1.4em;
          font-weight: bold;
          color: #4CAF50;
        }

        .price-steam-discount {
          display: inline-block;
          background: rgba(76, 175, 80, 0.2);
          color: #4CAF50;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.8em;
          font-weight: 600;
        }

        .price-unavailable {
          text-align: center;
        }

        .price-na {
          display: inline-block;
          color: var(--text-secondary);
          font-size: 0.9em;
          opacity: 0.7;
        }

        .discount-stats {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin: 12px 0;
          padding: 12px;
          background: var(--card-bg-secondary);
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }

        .discount-stat {
          display: flex;
          justify-content: space-between;
          font-size: 0.9em;
        }

        .stat-label {
          color: var(--text-secondary);
        }

        .stat-value {
          color: var(--text-primary);
          font-weight: 600;
          font-size: 0.9em;
        }

        .time-remaining {
          color: var(--accent-red);
          font-weight: bold;
        }

        .discount-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid var(--border-color);
        }

        .discount-user {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9em;
          color: var(--text-secondary);
        }

        .user-link {
          color: var(--btn-primary);
          text-decoration: none;
          font-weight: 600;
        }

        .user-link:hover {
          text-decoration: underline;
        }

        .discount-actions {
          display: flex;
          gap: 6px;
        }

        .btn {
          padding: 4px 10px;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          font-weight: 500;
          text-decoration: none;
          display: inline-block;
          transition: all 0.2s;
          font-size: 0.75em;
        }

        .btn-primary {
          background: var(--btn-primary);
          color: white;
        }

        .btn-primary:hover {
          background: var(--btn-primary-hover);
        }

        .btn-danger {
          background: var(--btn-danger);
          color: white;
          padding: 4px 8px;
        }

        .btn-danger:hover {
          background: var(--btn-danger-hover);
        }

        .btn-sm {
          font-size: 0.75em;
          padding: 4px 10px;
        }

        .btn-icon {
          padding: 4px 7px;
          font-size: 0.85em;
        }
      `}</style>
    </div>
  );
};

export default DiscountCard;
